import { type ReactNode } from 'react'
import {
  Box,
  Card,
  Center,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core'
import type {
  AnalyticsDashboardStats,
} from '@/types/api'
import {
  analyticsTooltipStyles,
  compactChangeLine,
  compactSummaryText,
  currentColor,
  formatDurationClock,
  formatNumber,
  previousColor,
  summaryTone,
} from '@/pages/analytics/analytics-new-utils'
import {
  UnifiedTooltip,
} from '@/pages/analytics/analytics-tooltips'
export {
  AnalyticsTooltipProvider,
  AnalyticsTooltipContent,
  ChartRowsTooltipContent,
  ProductChartTooltipContent,
  ProductTooltipRow,
  UnifiedTooltip,
} from '@/pages/analytics/analytics-tooltips'
export {
  AiTransferTicketsModal,
  ProductTicketsModal,
} from '@/pages/analytics/analytics-modals'
export {
  AxisIconRow,
  LegendAiProjectMetric,
  LegendAiProjectRow,
  LegendAiReasonRow,
  LegendKtuRow,
  LegendOperatorRow,
  LegendProductRow,
  LegendSourceRow,
} from '@/pages/analytics/analytics-legends'

export function MetricTile({ label, value, tone = 'default', hint, compact = false }: { label: string; value: ReactNode; tone?: 'default' | 'success' | 'danger'; hint?: ReactNode; compact?: boolean }) {
  const background = tone === 'success' ? 'var(--mantine-color-green-5)' : tone === 'danger' ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-gray-1)'
  const color = tone === 'danger' || tone === 'success' ? 'var(--mantine-color-white)' : 'var(--mantine-color-dark-7)'
  return (
    <Card radius="md" padding={compact ? 'md' : 'lg'} style={{ minHeight: compact ? 58 : 64, background, border: '1px solid var(--mantine-color-gray-2)' }}>
      <Center h="100%">
        <Stack gap={2} align="center">
          <Group gap={6} wrap="nowrap" align="center" justify="center">
            <Text fw={600} size={compact ? 'xs' : 'sm'} ta="center" c={color} lh={1.2}>{label}</Text>
            {hint ? <InfoHint label={hint} tone={tone} /> : null}
          </Group>
          {typeof value === 'string' || typeof value === 'number' ? (
            <Text fw={800} size={compact ? 'lg' : 'xl'} ta="center" c={color} lh={1.2}>{value}</Text>
          ) : (
            value
          )}
        </Stack>
      </Center>
    </Card>
  )
}

export function MainInfoCard({
  label,
  value,
  tone = 'default',
  delta,
  tooltip,
}: {
  label: string
  value: ReactNode
  tone?: 'default' | 'success' | 'danger' | 'warning'
  delta?: { text: string; tone: 'green' | 'red' | 'gray' } | null
  tooltip?: ReactNode
}) {
  const background = tone === 'warning' ? '#fff0db' : tone === 'success'
    ? '#e4f8e7'
    : tone === 'danger'
      ? '#fde8e8'
      : '#f7f8fa'
  const labelColor = tone === 'warning' ? '#a34c00' : tone === 'success'
    ? '#008b17'
    : tone === 'danger'
      ? '#d10f0f'
      : '#8293a3'
  const valueColor = tone === 'warning' ? '#c45d00' : tone === 'success'
    ? '#009218'
    : tone === 'danger'
      ? '#d10f0f'
      : '#050505'
  const valueContent = typeof value === 'string' || typeof value === 'number'
    ? (
      <Text fw={500} c={valueColor} lh={1} style={{ fontSize: 22, letterSpacing: 0 }}>
        {value}
      </Text>
    )
    : (
      <Box style={{ color: valueColor, minWidth: 0 }}>
        {value}
      </Box>
    )
  const card = (
    <Card
      radius={6}
      padding="sm"
      style={{
        minHeight: 76,
        height: '100%',
        background,
        border: '1px solid rgba(15, 23, 42, 0.03)',
        boxShadow: 'none',
        cursor: tooltip ? 'help' : undefined,
      }}
    >
      <Stack h="100%" gap={8} justify="space-between">
        <Text fw={500} c={labelColor} lh={1.15} style={{ fontSize: 12, letterSpacing: 0 }}>
          {label}
        </Text>
        <Group justify="space-between" align="flex-end" gap={8} wrap={tone === 'warning' ? 'wrap' : 'nowrap'}>
          {valueContent}
          {delta ? (
            <Text fw={700} c={delta.tone === 'green' ? '#008b17' : delta.tone === 'red' ? '#d10f0f' : '#8293a3'} ta="right" lh={1.1} style={{ fontSize: 10, letterSpacing: 0 }}>
              {delta.text}
            </Text>
          ) : null}
        </Group>
      </Stack>
    </Card>
  )
  if (!tooltip) return card
  return (
    <UnifiedTooltip label={tooltip} styles={analyticsTooltipStyles} wrapperStyle={{ display: 'block', minWidth: 0, height: '100%' }}>
      <Box style={{ display: 'block', minWidth: 0, height: '100%' }}>
        {card}
      </Box>
    </UnifiedTooltip>
  )
}

export function MainInfoSummary({
  stats,
  prevStats,
  loading,
}: {
  stats: AnalyticsDashboardStats | null
  prevStats: AnalyticsDashboardStats | null
  loading: boolean
}) {
  const currentStatus = stats?.by_status ?? {}
  const previousStatus = prevStats?.by_status ?? {}
  return (
    <Box pos="relative">
      {loading ? <BlockLoader label="Загружаем основные показатели" /> : null}
      <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }} spacing="sm" opacity={loading ? 0.45 : 1}>
        <MainInfoCard
          label="Всего обращений"
          value={formatNumber(stats?.total_tickets)}
          delta={compactChangeLine(stats?.total_tickets, prevStats?.total_tickets, true)}
        />
        <SimpleGrid cols={2} spacing="xs" style={{ height: '100%' }}>
          <MainInfoCard
            label="Обращений в авариях"
            value={formatNumber(stats?.emergency_tickets_in_period)}
            tone="warning"
            delta={compactChangeLine(stats?.emergency_tickets_in_period, prevStats?.emergency_tickets_in_period, true)}
          />
          <MainInfoCard
            label="Массово закрыты"
            value={formatNumber(stats?.bulk_closed_in_period)}
            tone="warning"
            delta={compactChangeLine(stats?.bulk_closed_in_period, prevStats?.bulk_closed_in_period)}
          />
        </SimpleGrid>
        <MainInfoCard
          label="Закрыто операторами"
          value={formatNumber(stats?.closed_by_operators)}
          delta={compactChangeLine(stats?.closed_by_operators, prevStats?.closed_by_operators)}
        />
        <MainInfoCard
          label="Среднее время ответа"
          value={formatDurationClock(stats?.metrics?.avg_first_response_time_minutes)}
          delta={compactChangeLine(
            stats?.metrics?.avg_first_response_time_minutes,
            prevStats?.metrics?.avg_first_response_time_minutes,
            true,
          )}
        />
        <MainInfoCard
          label="Аварии"
          value={formatNumber(stats?.emergency_modes_count)}
          tone="danger"
          delta={compactChangeLine(stats?.emergency_modes_count, prevStats?.emergency_modes_count, true)}
        />
        <MainInfoCard
          label="Закрыто"
          value={formatNumber(currentStatus.closed)}
          delta={compactChangeLine(currentStatus.closed, previousStatus.closed)}
        />
        <MainInfoCard
          label="Закрыто мозгоботом"
          value={formatNumber(stats?.closed_by_ai)}
          delta={compactChangeLine(stats?.closed_by_ai, prevStats?.closed_by_ai)}
        />
        <MainInfoCard
          label="Среднее время решения"
          value={formatDurationClock(stats?.metrics?.avg_resolution_time_minutes)}
          delta={compactChangeLine(
            stats?.metrics?.avg_resolution_time_minutes,
            prevStats?.metrics?.avg_resolution_time_minutes,
            true,
          )}
        />
      </SimpleGrid>
    </Box>
  )
}

export function ChartInfoBlock({
  label,
  value,
  tone = 'neutral',
  tooltip,
}: {
  label: string
  value: ReactNode
  tone?: 'neutral' | 'good' | 'bad'
  tooltip?: ReactNode
}) {
  const background =
    tone === 'good'
      ? '#e4f8e7'
      : tone === 'bad'
        ? '#fde8e8'
        : '#f7f8fa'
  const labelColor =
    tone === 'good'
      ? '#3fb35b'
      : tone === 'bad'
        ? '#e05a5a'
        : '#94a3b8'
  const valueColor =
    tone === 'good'
      ? '#008b17'
      : tone === 'bad'
        ? '#d10f0f'
        : '#050505'
  const block = (
    <Card
      radius={6}
      padding="xs"
      style={{
        width: 136,
        minHeight: 56,
        background,
        border: '1px solid rgba(15, 23, 42, 0.03)',
        boxShadow: 'none',
        flex: '0 0 136px',
        cursor: tooltip ? 'help' : undefined,
      }}
    >
      <Stack gap={6}>
        <Text fw={500} c={labelColor} lh={1.15} style={{ fontSize: 11, letterSpacing: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </Text>
        <Text fw={700} c={valueColor} lh={1.1} style={{ fontSize: 15, letterSpacing: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </Text>
      </Stack>
    </Card>
  )
  if (!tooltip) return block
  return (
    <UnifiedTooltip label={tooltip} styles={analyticsTooltipStyles} wrapperStyle={{ display: 'block', flex: '0 0 136px' }}>
      {block}
    </UnifiedTooltip>
  )
}

export function InfoHint({ label, tone = 'default' }: { label: ReactNode; tone?: 'default' | 'success' | 'danger' | 'warning' }) {
  const fg = tone === 'danger' || tone === 'success' ? 'rgba(255,255,255,0.92)' : '#64748b'
  const bg = tone === 'danger' || tone === 'success' ? 'rgba(255,255,255,0.18)' : 'rgba(148, 163, 184, 0.18)'
  const border = tone === 'danger' || tone === 'success' ? 'rgba(255,255,255,0.24)' : 'rgba(148, 163, 184, 0.3)'
  return (
    <UnifiedTooltip label={label} styles={analyticsTooltipStyles} wrapperStyle={{ display: 'inline-flex' }}>
      <Center
        w={16}
        h={16}
        style={{ borderRadius: 999, background: bg, border: `1px solid ${border}`, color: fg, fontSize: 10, fontWeight: 700, cursor: 'help', flexShrink: 0 }}
      >
        i
      </Center>
    </UnifiedTooltip>
  )
}

export function BlockLoader({ label }: { label: string }) {
  return (
    <Center
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        borderRadius: 12,
        background: 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <Stack gap={6} align="center">
        <Loader size="sm" />
        <Text size="xs" c="dimmed">{label}</Text>
      </Stack>
    </Center>
  )
}

export function AnalyticsChartCard({ title, titleHint, summary, children, right, legendItems, topNotice, loading, loadingLabel = 'Загружаем данные', withoutAI, onWithoutAIChange }: { title: string; titleHint?: ReactNode; summary: { text: string; tone: 'green' | 'red' | 'gray' }; children: ReactNode; right: ReactNode[]; legendItems?: Array<{ label: string; color: string }>; topNotice?: string; loading?: boolean; loadingLabel?: string; withoutAI?: boolean; onWithoutAIChange?: (checked: boolean) => void }) {
  const footerLegend = legendItems ?? [
    { label: 'текущий период', color: currentColor },
    { label: 'предыдущий период', color: previousColor },
  ]
  return (
    <Card withBorder radius={18} padding={20} pos="relative" style={{ flex: '1 1 520px', minWidth: 0, maxWidth: 'calc(50% - 8px)', borderColor: 'var(--border-color)', background: 'var(--bg-primary)', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)', overflow: 'hidden' }}>
      {loading ? <BlockLoader label={loadingLabel} /> : null}
      <Group justify="space-between" align="flex-start" mb={16}>
        <Stack gap={14}>
          <Group gap={8} align="center">
            {titleHint ? (
              <UnifiedTooltip label={titleHint} maw={420} styles={analyticsTooltipStyles} wrapperStyle={{ display: 'inline-flex' }}>
                <Text fw={700} style={{ fontSize: 16, lineHeight: '22px', letterSpacing: '-0.01em', color: 'var(--text-primary)', cursor: 'help' }}>{title}</Text>
              </UnifiedTooltip>
            ) : (
              <Text fw={700} style={{ fontSize: 16, lineHeight: '22px', letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>{title}</Text>
            )}
          </Group>
          <Group gap={8} wrap="wrap">
            <ChartInfoBlock
              label="Динамика"
              value={compactSummaryText(summary)}
              tone={summaryTone(summary)}
              tooltip={topNotice}
            />
          </Group>
        </Stack>
        {onWithoutAIChange ? <AnalyticsAiToggle checked={Boolean(withoutAI)} onChange={onWithoutAIChange} /> : null}
      </Group>
      <Group align="flex-start" gap={16} wrap="nowrap" opacity={loading ? 0.45 : 1}>
        <Box style={{ flex: '1 1 auto', minWidth: 0 }}>
          <Box style={{ height: 'clamp(180px, 22vh, 250px)' }}>{children}</Box>
          <Group justify="space-between" align="flex-end" mt={14} wrap="nowrap">
            <Stack gap={8}>
              {footerLegend.map((item) => (
                <Group key={item.label} gap={10} wrap="nowrap">
                  <Box w={14} h={14} bg={item.color} style={{ borderRadius: 4, flexShrink: 0 }} />
                  <Text style={{ fontSize: 12, lineHeight: '16px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>— {item.label}</Text>
                </Group>
              ))}
            </Stack>
          </Group>
        </Box>
        {right.length > 0 ? (
          <Stack gap={10} style={{ width: 'clamp(210px, 28%, 280px)', maxHeight: 'clamp(220px, 28vh, 340px)', overflowY: 'auto', flex: '0 0 clamp(210px, 28%, 280px)', paddingRight: 6 }}>{right}</Stack>
        ) : null}
      </Group>
    </Card>
  )
}

export function AnalyticsInlineToggle({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }) {
  return (
    <Group component="button" gap={8} align="center" onClick={() => onChange(!checked)} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
      <Center w={16} h={16} style={{ borderRadius: 4, background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: 11, fontWeight: 800, lineHeight: 1 }}>
        {checked ? '✓' : ''}
      </Center>
      <Text fw={700} style={{ fontSize: 12, lineHeight: '14px', color: 'var(--text-primary)' }}>{label}</Text>
    </Group>
  )
}

export function AnalyticsAiToggle({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return <AnalyticsInlineToggle checked={checked} onChange={onChange} label="Без Мозгобота" />
}
